import { useCallback, useMemo } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Form,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { EMPTY_LIST } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import {
  MAX_TERMINALS,
  MIN_TERMINALS,
  SITE_FIELDS,
  emptyTerminalValues,
  nextTerminalKey,
} from '../../constants/sites.constants';
import { TerminalFieldset } from './TerminalFieldset';
import styles from './SiteTerminalFields.module.scss';

export const SiteTerminalFields = () => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const terminalKeys = values[SITE_FIELDS.terminalKeys] ?? EMPTY_LIST;

  const addTerminal = useCallback(() => {
    const terminalKey = nextTerminalKey(terminalKeys);
    Object.entries(emptyTerminalValues(terminalKey)).forEach(([name, value]) =>
      setValue(name, value),
    );
    setValue(SITE_FIELDS.terminalKeys, [...terminalKeys, terminalKey]);
  }, [terminalKeys, setValue]);

  const removeTerminal = useCallback(
    (terminalKey) =>
      setValue(
        SITE_FIELDS.terminalKeys,
        terminalKeys.filter((key) => key !== terminalKey),
      ),
    [terminalKeys, setValue],
  );

  const addIcon = useMemo(() => <Plus size={16} />, []);
  const isFull = terminalKeys.length >= MAX_TERMINALS;

  return (
    <>
      {terminalKeys.map((terminalKey, index) => (
        <TerminalFieldset
          key={terminalKey}
          terminalKey={terminalKey}
          heading={t('SITES_TERMINAL_HEADING', { index: index + 1 })}
          canRemove={terminalKeys.length > MIN_TERMINALS}
          onRemove={removeTerminal}
        />
      ))}

      <Form.Row columns={3}>
        <Button
          fullWidth
          className={styles.addTerminal}
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          disabled={isFull}
          onClick={addTerminal}
        >
          {t('SITES_ADD_TERMINAL')}
        </Button>
      </Form.Row>
    </>
  );
};
